import React from "react";

/**
 * Iter 36 — "Building your profile" placeholder.
 * Renders in place of statistically-noisy metrics when the user has
 * fewer than MIN_TRADES_FOR_INSIGHT total trades. Visual tag style
 * mirrors the "Early estimate" pill on the Health Score gauge for
 * consistency across the dashboard.
 *
 * Uses span-based wrappers so it's safe to drop into a parent <p>
 * (e.g. MetricTile renders `value` inside a paragraph).
 *
 * Props:
 *   - tradesRemaining: number — how many more trades to unlock
 *   - variant: "block" | "inline"
 *       block  → pill on top, message below (stacked, for metric tiles)
 *       inline → single horizontal line (for compact cards)
 *   - testId: optional data-testid passed to the wrapper
 */
export default function BuildingProfileNotice({
  tradesRemaining = 0,
  variant = "block",
  testId,
}) {
  const remaining = Math.max(0, tradesRemaining);
  const tradeWord = remaining === 1 ? "trade" : "trades";
  const message = `Log ${remaining} more ${tradeWord} to unlock this`;

  const Pill = (
    <span
      className="text-[10px] font-normal normal-case tracking-normal px-2 py-0.5 rounded-full border whitespace-nowrap inline-block"
      style={{
        color: "var(--bt-text-muted)",
        borderColor: "var(--bt-border)",
        background: "var(--bt-surface-2)",
      }}
    >
      Building your profile
    </span>
  );

  if (variant === "inline") {
    return (
      <span
        data-testid={testId}
        className="inline-flex flex-wrap items-center gap-2 py-2"
      >
        {Pill}
        <span className="text-sm font-normal" style={{ color: "var(--bt-text-muted)" }}>
          {message}
        </span>
      </span>
    );
  }

  return (
    <span data-testid={testId} className="inline-flex flex-col items-start gap-2 w-full">
      {Pill}
      <span
        className="text-sm font-normal leading-snug"
        style={{ color: "var(--bt-text-muted)" }}
      >
        {message}
      </span>
    </span>
  );
}
